/*
  Savendo live counters — views / cart adds / watchers.

  Reports three real events to the counter worker and renders what it returns.
  It never computes, estimates or embellishes a number: the DOM only ever shows
  integers the server sent back, and a row stays hidden while its count is zero.

  PRIVACY / CONSENT
  The visitor id is a random UUID in localStorage. It contains no personal data
  and is never joined to an order or a customer record. It is still a persistent
  identifier though, so for EU/UK traffic it belongs behind analytics consent.
  Shopify exposes that via the Customer Privacy API — if you enable a consent
  banner, gate hasConsent() below on:

      window.Shopify.customerPrivacy.analyticsProcessingAllowed()

  and mention the identifier in your privacy policy.
*/
(function () {
  var VISITOR_KEY = 'savendo:visitor';

  function hasConsent() {
    // Replace with the Customer Privacy API check once a consent banner is live.
    return true;
  }

  function visitorId() {
    try {
      var existing = window.localStorage.getItem(VISITOR_KEY);
      if (existing) return existing;
      var id =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : String(Date.now()) + '-' + Math.random().toString(36).slice(2);
      window.localStorage.setItem(VISITOR_KEY, id);
      return id;
    } catch (error) {
      return null; // storage blocked — we simply do not count this visitor
    }
  }

  /*
    Strongest signal first. Cart adds beat views because they show intent;
    watchers come last because the figure moves slowest.

    The labels say exactly what the worker measures. "added to cart today" is
    not shortened to "in carts" — that would claim those carts still exist,
    which is a different thing and is not what is being counted.
  */
  /*
    `min` is the real count a metric must reach before it is worth showing.
    It never alters the number displayed — the figure is always the true one
    from the worker. It only decides whether that figure is interesting
    enough to print, and the badge falls through to the next metric (or
    hides) when the bar is not met.

    Why they differ: a view is passive and cheap, so "1 viewed today" reads
    as "nobody is here" and argues against buying. Five is the point where it
    starts sounding like activity. A cart add is a deliberate act of intent,
    so even one is worth saying — "In 1 cart" is a real signal.
  */
  var METRICS = [
    { key: 'in_carts', min: 1, prefix: 'In', label: 'carts', icon: false, pulse: false },
    { key: 'views_today', min: 5, prefix: '', label: 'viewed today', icon: false, pulse: true },
    { key: 'watchers', min: 1, prefix: '', label: 'watching', icon: true, pulse: false },
  ];

  function render(root, data) {
    if (!data) return;

    /*
      Emitted before the badge decides anything, because the heart's count is
      not the badge's count.

      This dispatch used to sit at the end of the function, below the early
      return taken when no metric clears its threshold. Unwatching the only
      watcher takes watchers to 0, which clears nothing, so render() hid the
      badge and returned — and the event that tells the heart to drop its
      number never fired. The badge vanished correctly while the button kept
      showing a stale "1" until the next page load.

      Zero is a real value here and the listener in savendo-product.js already
      handles it by hiding the count, so it has to be told.
    */
    document.dispatchEvent(
      new CustomEvent('savendo:counts', {
        detail: { watching: data.watching, watchers: Number(data.watchers) || 0 },
      })
    );

    var chosen = null;
    for (var i = 0; i < METRICS.length; i++) {
      var count = Number(data[METRICS[i].key]);
      // Below its bar the metric is not a story worth telling — fall through
      // to the next one, and hide the badge entirely if none qualify.
      if (isFinite(count) && count >= METRICS[i].min) {
        chosen = { meta: METRICS[i], count: count };
        break;
      }
    }

    if (!chosen) {
      root.hidden = true;
      return;
    }

    var prefix = root.querySelector('[data-sv-counts-prefix]');
    prefix.textContent = chosen.meta.prefix;
    prefix.hidden = !chosen.meta.prefix;

    root.querySelector('[data-sv-counts-value]').textContent = chosen.count;
    root.querySelector('[data-sv-counts-label]').textContent =
      chosen.meta.key === 'in_carts' && chosen.count === 1 ? 'cart' : chosen.meta.label;
    root.querySelector('[data-sv-counts-icon]').hidden = !chosen.meta.icon;
    root.querySelector('[data-sv-counts-pulse]').hidden = !chosen.meta.pulse;
    root.hidden = false;
  }

  function init() {
    var root = document.querySelector('[data-sv-counts]');
    if (!root || root.dataset.svBound === 'true') return;

    var endpoint = root.dataset.endpoint;
    var productId = root.dataset.productId;
    if (!endpoint || !productId || !hasConsent()) return;

    root.dataset.svBound = 'true';
    var visitor = visitorId();
    if (!visitor) return;

    function send(kind) {
      return fetch(endpoint.replace(/\/$/, '') + '/event', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ product: productId, visitor: visitor, kind: kind }),
        keepalive: true,
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          render(root, data);
          return data;
        })
        .catch(function () {
          // Counter offline: show nothing rather than a stale or invented figure.
          return null;
        });
    }

    /*
      Report the visitor's whole cart, not an "added" event. The worker replaces
      their rows with exactly this set, which is what lets "In 3 carts" mean
      three carts hold it right now — remove it or check out and the row goes.
    */
    function syncCart() {
      return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { accept: 'application/json' },
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (!cart) return null;
          var products = (cart.items || []).map(function (item) {
            return String(item.product_id);
          });
          return fetch(endpoint.replace(/\/$/, '') + '/cart', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ visitor: visitor, product: productId, products: products }),
            keepalive: true,
          });
        })
        .then(function (response) {
          return response && response.ok ? response.json() : null;
        })
        .then(function (data) {
          render(root, data);
          return data;
        })
        .catch(function () {
          return null;
        });
    }

    // A page load is one view, and a chance to re-sync cart state.
    send('view');
    syncCart();

    // Any cart change anywhere — add, quantity change, remove — re-syncs.
    if (typeof window.subscribe === 'function') {
      window.subscribe('cart-update', syncCart);
    }

    // Watch toggles, emitted by savendo-product.js.
    document.addEventListener('savendo:watch', function (event) {
      if (!event.detail) return;
      send(event.detail.watching ? 'watch' : 'unwatch');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
